import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Rocket, Sparkles, Eye, LucideIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getBookAwards,
  getPrimaryAward,
  AWARD_CONFIG,
  SFAward,
  AwardType
} from '@/constants/sfAwards';

/**
 * Single source of truth for award iconography.
 *
 * AWARD_CONFIG has always declared `icon: 'rocket' | 'sparkles' | 'eye'`, but
 * nothing consumed it — these badges fell back to the `emoji` field, so the
 * Signal Collection rendered the OS emoji 🚀 while the Community showcase used
 * the Lucide line icons. Same award, two different marks. Everything now maps
 * through here, so the two surfaces match.
 */
export const AWARD_ICONS: Record<AwardType, LucideIcon> = {
  hugo: Rocket,
  nebula: Sparkles,
  pkd: Eye,
};

interface AwardBadgeProps {
  title: string;
  author?: string;
  size?: 'sm' | 'md';
  showAll?: boolean;
  className?: string;
}

/**
 * Displays award badges for books that have won Hugo, Nebula, or PKD awards
 */
export const AwardBadge: React.FC<AwardBadgeProps> = ({ 
  title, 
  author, 
  size = 'sm', 
  showAll = false,
  className = '' 
}) => {
  const awards = getBookAwards(title, author);
  
  if (awards.length === 0) return null;
  
  const displayAwards = showAll ? awards : [getPrimaryAward(title, author)!];
  const uniqueTypes = [...new Set(displayAwards.map(a => a.type))];
  
  if (size === 'sm') {
    // Compact single-line badge for card views
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* A Link, not a div. This was display-only, so the mark on a card
                looked identical to the one in the preview but did nothing when
                pressed — and an award is a reason to go and look at the rest of
                that award's list. Deep-links to this exact title, like the md
                variant, so both sizes behave the same way. */}
            <Link
              to={`/community?award=${uniqueTypes[0]}&title=${encodeURIComponent(title)}#awards`}
              onClick={(e) => e.stopPropagation()}
              aria-label={`${title} — see it in the ${AWARD_CONFIG[uniqueTypes[0]].name} list`}
              className={`inline-flex items-center gap-1 rounded hover:opacity-80 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/60 transition-opacity ${className}`}
            >
              {uniqueTypes.slice(0, 2).map(type => {
                const Icon = AWARD_ICONS[type];
                return (
                  <Icon
                    key={type}
                    className={`w-3.5 h-3.5 ${AWARD_CONFIG[type].color}`}
                    aria-label={AWARD_CONFIG[type].name}
                  />
                );
              })}
              {awards.some(a => a.isWinner) && (
                <Award className="w-3 h-3 text-amber-400" />
              )}
            </Link>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="space-y-1">
              {displayAwards.map((award, idx) => {
                const Icon = AWARD_ICONS[award.type];
                return (
                  <div key={idx} className="text-xs flex items-center gap-1">
                    <Icon className={`w-3 h-3 shrink-0 ${AWARD_CONFIG[award.type].color}`} />
                    <span className={AWARD_CONFIG[award.type].color}>
                      {AWARD_CONFIG[award.type].name}
                    </span>
                    <span className="text-muted-foreground">
                      {award.isWinner ? 'Winner' : 'Nominee'} ({award.year})
                    </span>
                  </div>
                );
              })}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  // Medium size — glyph + text, matching the Community award showcase headers.
  // No pill: just the award mark and label, and it links through to the
  // Award-Winning Sci-Fi list filtered to that award.
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${className}`}>
      {displayAwards.map((award, idx) => {
        const Icon = AWARD_ICONS[award.type];
        const config = AWARD_CONFIG[award.type];
        return (
          /* Carry the TITLE through, not just the award.
             Landing on the award's list still left the reader hunting for the
             book they clicked from — and for a nominee it was worse than that,
             because the showcase filters to winners, so the book was not on the
             page at all. The showcase reads `title` and brings that book to the
             front. */
          <Link
            key={idx}
            to={`/community?award=${award.type}&title=${encodeURIComponent(title)}#awards`}
            className="group inline-flex items-center gap-1.5"
            title={`See ${title} in the ${config.name} list`}
          >
            <Icon className={`w-4 h-4 ${config.color}`} />
            <span className={`text-xs font-semibold ${config.color} group-hover:underline underline-offset-4`}>
              {config.name}
            </span>
            <span className="text-xs text-slate-400">
              {award.isWinner ? 'Winner' : 'Nominee'} {award.year}
            </span>
          </Link>
        );
      })}
    </div>
  );
};


/**
 * Minimal award indicator for very compact spaces (just emoji)
 */
export const AwardIndicator: React.FC<{ title: string; author?: string; className?: string }> = ({ 
  title, 
  author,
  className = '' 
}) => {
  const primaryAward = getPrimaryAward(title, author);
  
  if (!primaryAward) return null;

  const Icon = AWARD_ICONS[primaryAward.type];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`cursor-help inline-flex ${AWARD_CONFIG[primaryAward.type].color} ${className}`}
            aria-label={`${AWARD_CONFIG[primaryAward.type].name} ${primaryAward.isWinner ? 'Winner' : 'Nominee'}`}
          >
            <Icon className="w-4 h-4" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            {AWARD_CONFIG[primaryAward.type].name} {primaryAward.isWinner ? 'Winner' : 'Nominee'} ({primaryAward.year})
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AwardBadge;
